import { Button, DatePicker, Flex, notification, Table, Typography, Card, List, Descriptions } from "antd";
import type { TableProps } from "antd";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { getRequestWithParams } from "../../../../api/httpRequest";
import { ITransfer } from "./model/transfers";
import { transfersConfig } from "./config";
import "./styles/styles.less";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import { userStore } from "../../../../store/store";
import { RiFileExcel2Fill } from "react-icons/ri";
import { useMediaQuery } from "react-responsive";
import * as XLSX from "xlsx";

export const MASKING = `${process.env.MASKING}`;

export default function Transfers() {
    const [transfers, setTransfers] = useState<ITransfer[]>([]);
    const [filteredTransfers, setFilteredTransfers] = useState<ITransfer[]>([]);
    const { user } = userStore();
    const [loading, setLoading] = useState(true);
    const [api, contextHolder] = notification.useNotification();
    const isMobile = useMediaQuery({ maxWidth: 768 });

    const fetchTransfers = async (year: dayjs.Dayjs | null) => {
        setLoading(true);
        getRequestWithParams(
            year ? "/server/proxy/salary/transfer/year" : "/server/proxy/salary/transfer",
            year ? { year: year.year() } : { }
        )
            .then(response => {
                let responseData;
                if (MASKING === "1") {
                    responseData = response.data.result.map((item: ITransfer) => ({
                        ...item,
                        SUMM: Math.floor(Math.random() * 1000) + 1,
                    }));
                } else {
                    responseData = response.data.result;
                }
                const sortedData = responseData.sort((item1: ITransfer, item2: ITransfer) =>
                    dayjs(item1.DATST).isAfter(dayjs(item2.DATST)) ? -1 : 1
                );
                setTransfers(sortedData);
                setFilteredTransfers(sortedData);
                setLoading(false);
            })
            .catch(error => {
                console.log(error);
                api.error({
                    message: "Ошибка загрузки переводов",
                    duration: 2,
                });
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchTransfers(dayjs());
    }, []);

    const columns = useMemo(() => transfersConfig(transfers), [transfers]);
    const summColumnIndex = useMemo(() => {
        return columns.findIndex(col => col.key === 'SUMM');
    }, [columns]);

    const totalSum = useMemo(() => {
        return filteredTransfers.reduce((sum, item) => {
            const numericValue = parseFloat(String(item.SUMM).replace(',', '.'));
            return sum + (isNaN(numericValue) ? 0 : numericValue);
        }, 0);
    }, [filteredTransfers]);

    // Типизированный обработчик изменений таблицы
    const handleTableChange: TableProps<ITransfer>['onChange'] = useCallback((pagination: any, filters: any, sorter: any, extra: any) => {
        setFilteredTransfers(extra.currentDataSource as ITransfer[]);
    }, []);

    const renderSummary = () => (
        <Table.Summary fixed="bottom">
            <Table.Summary.Row>
                {columns.map((col, idx) => (
                    <Table.Summary.Cell key={col.key || idx} index={idx}>
                        {idx === summColumnIndex ? (
                            <strong>Итого: {totalSum.toLocaleString()} ₽</strong>
                        ) : null}
                    </Table.Summary.Cell>
                ))}
            </Table.Summary.Row>
        </Table.Summary>
    );

    const handleExportExcel = () => {
        const exportData = transfers.map((item) => ({
            "ID": item.ID,
            "Тип": item.TYP,
            "Агентский список": item.AGNLIST,
            "TID": item.TID,
            "Дата": item.DATST,
            "Номер документа": item.DOCNUMB,
            "Год": item.YEAR,
            "Месяц": item.MONTH,
            "KA2": item.KA2,
            "KA3": item.KA3,
            "KA4": item.KA4,
            "Сумма": item.SUMM,
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Перечисления");
        XLSX.writeFile(workbook, "Переводы.xlsx");
    };

    const renderTransferCards = () => (
        <>
            <List
                loading={loading}
                dataSource={transfers}
                renderItem={(item, index) => (
                    <Card key={index} className="transfers-card">
                        <div className="card-header">
                            <Typography.Text className="card-date">
                                {dayjs(item.DATST).locale("ru").format("D MMMM YYYY")}
                            </Typography.Text>
                            <Typography.Text className="card-sum">
                                {item.SUMM} ₽
                            </Typography.Text>
                        </div>
                        <Descriptions size="small" column={1} bordered>
                            <Descriptions.Item label="Тип">{item.TYP}</Descriptions.Item>
                            <Descriptions.Item label="Агентский список">{item.AGNLIST}</Descriptions.Item>
                            <Descriptions.Item label="Номер документа">{item.DOCNUMB}</Descriptions.Item>
                            <Descriptions.Item label="Год">{item.YEAR}</Descriptions.Item>
                            <Descriptions.Item label="Месяц">{item.MONTH}</Descriptions.Item>
                            <Descriptions.Item label="KA2">{item.KA2}</Descriptions.Item>
                            <Descriptions.Item label="KA3">{item.KA3}</Descriptions.Item>
                            <Descriptions.Item label="KA4">{item.KA4}</Descriptions.Item>
                        </Descriptions>
                    </Card>
                )}
            />
            <div style={{ marginTop: 16, textAlign: 'right', fontWeight: 'bold' }}>
                Итого: {totalSum.toLocaleString()} ₽
            </div>
        </>
    );

    return (
        <Flex className="transfers" vertical>
            {contextHolder}
            <Flex justify="space-between" className="transfers-filters" wrap="wrap" gap={8}>
                <Flex gap={16} align="center" wrap="wrap" style={{ flex: 1 }}>
                    <Typography.Text>Год:</Typography.Text>
                    <DatePicker
                        onChange={(value) => fetchTransfers(value)}
                        picker="year"
                        defaultValue={dayjs().locale("ru")}
                        disabledDate={(current) => current && (current.year() > dayjs().year() || current.year() < 2022)}
                    />
                </Flex>
                <Button
                    type="text"
                    icon={<RiFileExcel2Fill color="#40a202" size={20} />}
                    onClick={handleExportExcel}
                >
                    Экспорт в XLSX
                </Button>
            </Flex>

            {isMobile ? (
                renderTransferCards()
            ) : (
                <Table
                    style={{ border: "1px #a6a6a6 solid", borderStyle: "none solid solid none" }}
                    bordered
                    size="small"
                    loading={loading}
                    pagination={false}
                    dataSource={transfers}
                    scroll={{ y: 700 }}
                    columns={columns}
                    rowClassName={(_, index) =>
                        index % 2 === 0 ? "transfers-darker-row" : "transfers-row"
                    }
                    onChange={handleTableChange}
                    summary={renderSummary}
                />
            )}
        </Flex>
    );
}